import { useEffect, useMemo, useRef, useState } from 'react';

import { refreshAccessTokenNow } from '@/api/api';
import { getAccessToken, isAccessTokenExpired } from '@/api/tokens.helper';
import { MEDIA_POLL_INTERVAL } from '@/common/media';
import { isStreamableFileLink, signStreamingLink } from '@/utils/file-links';
import { isDecodeMediaError, isUnplayableMediaError } from '@/utils/video-playback';

type Resume = { time: number; playing: boolean };

/** What a probe of a link learned: the encoder still has it, or it is there to play. */
type Probe = { link: string; state: 'processing' | 'ready' };

/**
 * Asks the link for its status alone. The request is abandoned as soon as the
 * headers are in, so a file that is ready costs one round trip, not a download.
 * Signed into the URL like the element's own requests, so no header is added and
 * nothing has to be pre-flighted.
 */
const probeStatus = async (link: string) => {
  const abort = new AbortController();
  const response = await fetch(signStreamingLink(link), { signal: abort.signal });
  abort.abort();
  return response.status;
};

/**
 * A `src` a media element can stream a private link from, byte range by byte
 * range, without the file ever being fetched whole. Links that are not
 * streamable (a local `blob:` preview, a bundled asset) pass through as-is.
 *
 * The access token signed into the URL lives ten minutes and nothing renews
 * it there, so a clip left paused past that — or seeked into a part not yet
 * fetched — gets a 401 the element reports only as a bare `error` event. Hand
 * that event to `onError`: if the token has expired it is renewed — or, when the
 * app already renewed it, used as is — the link re-signed, and
 * `onLoadedMetadata` puts the playhead back where it was.
 *
 * A video the media encoder is still re-encoding answers 409, which the element
 * reports the same bare way. Then — or straight away when the caller already
 * knows the file is `pending` — there is no `src` and `processing` is set; the
 * link is probed every few seconds and handed back as soon as it can be played,
 * whether or not anybody told the caller the file is ready. Any other failure (a
 * deleted file, a codec this browser lacks) sets `error`.
 *
 * `onUnplayable` takes the codec case instead, when the caller has another
 * rendition to try (see `useBackupVideo`): a decode error goes straight to it,
 * and a "source not supported" one only after the token has been renewed if it
 * had to be and the link has answered with its bytes — so a 401 or a 409 is never
 * mistaken for a codec. It returns false when it had nothing left to switch to.
 */
export const useStreamingFileUrl = (
  link: null | string | undefined,
  {
    enabled = true,
    pending = false,
    onUnplayable,
  }: {
    enabled?: boolean;
    pending?: boolean;
    onUnplayable?: (media: HTMLMediaElement) => boolean;
  } = {},
) => {
  const [signature, setSignature] = useState(0);
  const [error, setError] = useState(false);
  const [probe, setProbe] = useState<null | Probe>(null);
  const resume = useRef<null | Resume>(null);

  const streamable = isStreamableFileLink(link);
  const probed = probe && probe.link === link ? probe.state : null;
  const processing =
    enabled && streamable && (probed === 'processing' || (pending && probed !== 'ready'));

  const src = useMemo(() => {
    if (!enabled || !link || processing) return null;
    return streamable ? signStreamingLink(link) : link;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a renewal re-signs with the fresh token
  }, [enabled, link, streamable, processing, signature]);

  useEffect(() => {
    if (!processing || !link) return;

    let cancelled = false;
    const timer = window.setInterval(() => {
      probeStatus(link)
        .then((status) => {
          if (cancelled || status === 409) return;
          setProbe({ link, state: 'ready' });
          setSignature((count) => count + 1);
        })
        .catch((err) => console.error(err));
    }, MEDIA_POLL_INTERVAL);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [processing, link]);

  const onError = (event: React.SyntheticEvent<HTMLMediaElement>) => {
    const media = event.currentTarget;
    const unplayable = isUnplayableMediaError(media.error);

    if (!streamable || !link) {
      if (!(unplayable && onUnplayable?.(media))) setError(true);
      return;
    }

    if (isDecodeMediaError(media.error) && onUnplayable?.(media)) return;

    // The token in the src, not the current one: the app renews tokens in the
    // background, so a src signed earlier can 401 while the current token is fine.
    const signedWith = new URL(media.currentSrc || media.src).searchParams.get('jwt');
    const expired = isAccessTokenExpired();

    if (resume.current || (!expired && signedWith === getAccessToken())) {
      probeStatus(link)
        .then((status) => {
          const served = status >= 200 && status < 300;
          if (status === 409) setProbe({ link, state: 'processing' });
          else if (!(served && unplayable && onUnplayable?.(media))) setError(true);
        })
        .catch(() => setError(true));
      return;
    }

    resume.current = { time: media.currentTime, playing: !media.paused };

    if (!expired) {
      setSignature((count) => count + 1);
      return;
    }

    void refreshAccessTokenNow().then((token) => {
      if (token) {
        setSignature((count) => count + 1);
      } else {
        resume.current = null;
        setError(true);
      }
    });
  };

  const onLoadedMetadata = (event: React.SyntheticEvent<HTMLMediaElement>) => {
    const pending = resume.current;
    if (!pending) return;
    resume.current = null;

    const media = event.currentTarget;
    media.currentTime = pending.time;
    if (pending.playing) void media.play();
  };

  return { src, error: error && !processing, processing, onError, onLoadedMetadata };
};
